import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { experimentalStyled } from '@mui/material/styles';
import DashboardNavbar from './DashboardNavbar';
import DashboardSidebar, { SIDEBAR_WIDTH_OPEN, SIDEBAR_WIDTH_CLOSED } from './DashboardSidebar';
import Logo from '../Logo';
import SessionIncomingAlert from './session/SessionIncomingAlert';
import QuickActions, { QUICK_ACTIONS_GUTTER } from './QuickActions';
import { toneAlpha } from '../../theme/tones';

const DashboardLayoutRoot = experimentalStyled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  // Deux halos très diffus derrière le contenu : la surface respire sans
  // jamais concurrencer la lisibilité des tableaux.
  backgroundImage: theme.palette.mode === 'light'
    ? `radial-gradient(1200px 600px at 12% -10%, ${toneAlpha('blue', 0.07)}, transparent 60%), radial-gradient(900px 500px at 100% 0%, ${toneAlpha('indigo', 0.05)}, transparent 55%)`
    : `radial-gradient(1200px 600px at 12% -10%, ${toneAlpha('blue', 0.1)}, transparent 60%), radial-gradient(900px 500px at 100% 0%, ${toneAlpha('orange', 0.06)}, transparent 55%)`,
  backgroundAttachment: 'fixed',
  display: 'flex',
  height: '100%',
  width: '100%'
}));

const DashboardLayoutWrapper = experimentalStyled('div')(({ theme, padding }) => ({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden',
  paddingTop: '64px',
  [theme.breakpoints.up('lg')]: {
    paddingLeft: padding,
    transition: 'padding-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  }
}));

const DashboardLayoutContainer = experimentalStyled('div')({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden'
});

const DashboardLayoutContent = experimentalStyled('div')(({ theme }) => ({
  flex: '1 1 auto',
  height: '100%',
  overflow: 'auto',
  position: 'relative',
  WebkitOverflowScrolling: 'touch',
  // Place réservée au bouton d'actions rapides, qui flotte par-dessus : sans
  // elle il recouvre le bas des pages, et notamment la pagination des
  // tableaux — on ne pouvait plus changer de page.
  paddingBottom: QUICK_ACTIONS_GUTTER.xs,
  [theme.breakpoints.up('md')]: {
    paddingBottom: QUICK_ACTIONS_GUTTER.md
  }
}));

const DashboardLayout = () => {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [isSidebarMobileOpen, setIsSidebarMobileOpen] = useState(false);
  const [padding, setPadding] = useState('');

  useEffect(() => {
    // Largeurs exportées par DashboardSidebar.js : une seule source de
    // vérité pour le rail, la barre haute et le décalage du contenu.
    setPadding(drawerOpen ? `${SIDEBAR_WIDTH_OPEN}px` : `${SIDEBAR_WIDTH_CLOSED}px`);
  }, [drawerOpen]);

  return (
    <DashboardLayoutRoot>
      {/* Logo rajouté mais affiché en tout petit pour que le svg logo dans le dashboardsidebar s'affiche totalement (vert inclus) */}
      <Logo
        sx={{
          height: 10,
          width: 10
        }}
      />
      <DashboardNavbar
        onSidebarMobileOpen={() => setIsSidebarMobileOpen(true)}
      />
      <DashboardSidebar
        onMobileClose={() => setIsSidebarMobileOpen(false)}
        openMobile={isSidebarMobileOpen}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
      />
      <DashboardLayoutWrapper padding={padding}>
        <DashboardLayoutContainer>
          <DashboardLayoutContent>
            <SessionIncomingAlert />
            <Outlet />
          </DashboardLayoutContent>
        </DashboardLayoutContainer>
      </DashboardLayoutWrapper>
      {/* Hors du conteneur défilant : le bouton garde sa place quelle que
          soit la page et sa position de défilement. */}
      <QuickActions />
    </DashboardLayoutRoot>
  );
};

export default DashboardLayout;
